import { isCommentInvoiceLine } from "./commentInvoiceLines";
function getPricingInvoiceItemKey(invoiceItem = {}, index = 0) {
	return invoiceItem?.lineId || invoiceItem?.id || `line-${index}`;
}

function getPricingInvoiceItemTotal(invoiceItem = {}) {
	if (isCommentInvoiceLine(invoiceItem)) return 0;
	const explicitValue = Number(invoiceItem?.value);
	if (Number.isFinite(explicitValue)) {
		return +explicitValue.toFixed(2);
	}

	const qty = Number(invoiceItem?.qty) || 0;
	const rate = Number(invoiceItem?.rate) || 0;
	const discount = Number.isFinite(Number(invoiceItem?.discount)) ? Number(invoiceItem.discount) : 1;
	return +(qty * rate * discount).toFixed(2);
}

function getAllowanceBudgetAmount(invoiceItem = {}) {
	const explicitAmount = Number(invoiceItem?.allowance?.amount);
	return Number.isFinite(explicitAmount)
		? +explicitAmount.toFixed(2)
		: getPricingInvoiceItemTotal(invoiceItem);
}

/**
 * Returns the canonical invoice total used by persistence and Price List.
 * Allowance rows contribute their fixed budget; linked selections contribute
 * only the amount that cumulatively exceeds that budget.
 */
export function computeBudgetAwarePricingTotal(invoiceItems = []) {
	if (!Array.isArray(invoiceItems)) {
		return 0;
	}

	const effectiveTotalsByKey = {};
	const budgetGroups = {};

	invoiceItems.forEach((invoiceItem, index) => {
		if (isCommentInvoiceLine(invoiceItem)) return;
		const lineKey = getPricingInvoiceItemKey(invoiceItem, index);
		const actualTotal = getPricingInvoiceItemTotal(invoiceItem);
		const isAllowance = invoiceItem?.lineType === "allowance";

		if (!isAllowance || !invoiceItem?.lineId) {
			effectiveTotalsByKey[lineKey] = actualTotal;
			return;
		}

		const budgetAmount = getAllowanceBudgetAmount(invoiceItem);
		budgetGroups[invoiceItem.lineId] = { budgetAmount, items: [] };
		effectiveTotalsByKey[lineKey] = budgetAmount;
	});

	invoiceItems.forEach((invoiceItem, index) => {
		if (isCommentInvoiceLine(invoiceItem)) return;
		const targetLineId = String(invoiceItem?.settlesContractLineId || "").trim();
		if (!targetLineId || invoiceItem?.lineType === "allowance" || !budgetGroups[targetLineId]) {
			return;
		}

		budgetGroups[targetLineId].items.push({
			lineKey: getPricingInvoiceItemKey(invoiceItem, index),
			actualTotal: getPricingInvoiceItemTotal(invoiceItem)
		});
	});

	Object.values(budgetGroups).forEach((group) => {
		let appliedTotal = 0;
		group.items.forEach((item) => {
			const previousOverage = Math.max(appliedTotal - group.budgetAmount, 0);
			appliedTotal = +(appliedTotal + item.actualTotal).toFixed(2);
			const nextOverage = Math.max(appliedTotal - group.budgetAmount, 0);
			effectiveTotalsByKey[item.lineKey] = +(nextOverage - previousOverage).toFixed(2);
		});
	});

	const total = Object.values(effectiveTotalsByKey).reduce((sum, value) => sum + (Number(value) || 0), 0);
	return +total.toFixed(2);
}
